import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { CalendarDays } from 'lucide-react';
import { Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { FocusPriority } from './types.js';

const { Text } = Typography;

export interface CalendarDeadline {
  id: string;
  title: string;
  date: string;
  project?: string;
  priority?: FocusPriority;
}

export interface CalendarWidgetProps {
  deadlines?: readonly CalendarDeadline[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (deadline: CalendarDeadline) => void;
}

function getDaysUntil(
  date: string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const diff = Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);
  if (diff < 0) {
    return t('crm.dashboard.calendar.overdue');
  }
  if (diff === 0) {
    return t('crm.dashboard.calendar.today');
  }
  if (diff === 1) {
    return t('crm.dashboard.calendar.tomorrow');
  }
  return t('crm.dashboard.calendar.in_days', { count: diff });
}

function getColorForDate(date: string): string {
  const diff = Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);
  if (diff < 0) {
    return 'var(--ant-color-error)';
  }
  if (diff <= 1) {
    return 'var(--ant-color-primary)';
  }
  if (diff <= 3) {
    return 'var(--ant-color-info)';
  }
  return 'var(--ant-color-text-tertiary)';
}

const PRIORITY_COLORS: Record<FocusPriority, string> = {
  P0: 'var(--ant-color-error)',
  P1: 'var(--ant-color-warning)',
  P2: 'var(--ant-color-text-tertiary)',
};

export function CalendarWidget({
  deadlines = [],
  className,
  testId,
  loading = false,
  onItemClick,
}: CalendarWidgetProps): ReactElement {
  const { t, locale } = useTranslation();

  const widgetContainerProps = {
    title: t('crm.dashboard.calendar.title'),
    icon: CalendarDays,
    iconColor: 'var(--ant-color-info)',
    minHeight: 180,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-3.5 flex flex-col gap-1.5">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-10 w-full"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : deadlines.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <CalendarDays size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.calendar.empty')}
          </span>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">
          {deadlines.map((deadline) => {
            const accentColor = getColorForDate(deadline.date);
            const parsedDate = new Date(deadline.date);
            const day = parsedDate.getDate();
            const month = parsedDate
              .toLocaleDateString(locale, { month: 'short' })
              .replace('.', '');

            return (
              <li key={deadline.id}>
                <button
                  type="button"
                  onClick={onItemClick ? () => onItemClick(deadline) : undefined}
                  className="transition-colors text-left border-0 flex w-full items-center gap-3 crm-hover-fill"
                  style={{
                    cursor: onItemClick ? 'pointer' : 'default',
                    padding: '4px 14px',
                    minHeight: 42,
                    borderBottom: '1px solid var(--ant-color-border-secondary)',
                    background: 'transparent',
                  }}
                >
                  <div
                    className="flex flex-col items-center justify-center crm-radius-s shrink-0"
                    style={{
                      width: 34,
                      height: 36,
                      border: `1px solid ${accentColor}`,
                      color: accentColor,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {day}
                    </span>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 500,
                        lineHeight: 1.2,
                        textTransform: 'uppercase',
                      }}
                    >
                      {month}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text
                      strong
                      className="truncate block"
                      style={{ fontSize: 13, color: 'var(--ant-color-text)', lineHeight: 1.25 }}
                    >
                      {deadline.title}
                    </Text>
                    {deadline.project ? (
                      <Text
                        type="secondary"
                        className="truncate"
                        style={{ fontSize: 10, display: 'block', marginTop: 1 }}
                      >
                        {deadline.project}
                      </Text>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 10,
                        color: accentColor,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {getDaysUntil(deadline.date, t)}
                    </span>
                    {deadline.priority ? (
                      <span
                        className="rounded-full"
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          lineHeight: 1,
                          padding: '2px 5px',
                          color: PRIORITY_COLORS[deadline.priority],
                          border: `1px solid ${PRIORITY_COLORS[deadline.priority]}`,
                        }}
                      >
                        {deadline.priority}
                      </span>
                    ) : null}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetContainer>
  );
}
